import { useState, useEffect } from 'react';

const PRESETS = [
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom' }
];

function getPresetDates(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  if (preset === 'last7days') {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return {
      fromDate: from.toISOString().slice(0, 10),
      toDate: today.toISOString().slice(0, 10),
      preset: 'last7days'
    };
  }
  
  if (preset === 'last30days') {
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return {
      fromDate: from.toISOString().slice(0, 10),
      toDate: today.toISOString().slice(0, 10),
      preset: 'last30days'
    };
  }
  
  if (preset === 'thisMonth') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      fromDate: from.toISOString().slice(0, 10),
      toDate: today.toISOString().slice(0, 10),
      preset: 'thisMonth'
    };
  }
  
  if (preset === 'lastMonth') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return {
      fromDate: from.toISOString().slice(0, 10),
      toDate: to.toISOString().slice(0, 10),
      preset: 'lastMonth'
    };
  }
  
  return { preset: 'custom' };
}

function getTodayString() {
  return new Date().toISOString().slice(0, 10);
}

export default function DateRangePicker({ fromDate, toDate, onChange }) {
  const [preset, setPreset] = useState('last7days');
  const [localFromDate, setLocalFromDate] = useState(fromDate || '');
  const [localToDate, setLocalToDate] = useState(toDate || '');
  
  useEffect(() => {
    if (fromDate && toDate) {
      const presetDates = getPresetDates(preset);
      if (fromDate === presetDates.fromDate && toDate === presetDates.toDate) {
        setLocalFromDate(fromDate);
        setLocalToDate(toDate);
      } else {
        setPreset('custom');
        setLocalFromDate(fromDate);
        setLocalToDate(toDate);
      }
    } else {
      const defaults = getPresetDates('last7days');
      setLocalFromDate(defaults.fromDate);
      setLocalToDate(defaults.toDate);
    }
  }, [fromDate, toDate]);
  
  const handlePresetChange = (e) => {
    const selectedPreset = e.target.value;
    setPreset(selectedPreset);
    
    if (selectedPreset !== 'custom') {
      const dates = getPresetDates(selectedPreset);
      setLocalFromDate(dates.fromDate);
      setLocalToDate(dates.toDate);
      onChange?.(dates.fromDate, dates.toDate);
    }
  };
  
  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setLocalFromDate(value);
    setPreset('custom');
    if (localToDate && value && localToDate >= value) {
      onChange?.(value, localToDate);
    }
  };
  
  const handleToDateChange = (e) => {
    const value = e.target.value;
    setLocalToDate(value);
    setPreset('custom');
    if (localFromDate && value && value >= localFromDate) {
      onChange?.(localFromDate, value);
    }
  };
  
  return (
    <div className="flex flex-wrap items-center gap-3">
      <select
        value={preset}
        onChange={handlePresetChange}
        className="input max-w-[160px]"
      >
        {PRESETS.map(p => (
          <option key={p.value} value={p.value}>{p.label}</option>
        ))}
      </select>
      
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={localFromDate}
          onChange={handleFromDateChange}
          max={localToDate || getTodayString()}
          className="input max-w-[150px]"
        />
        <span className="text-sm text-slate-400">to</span>
        <input
          type="date"
          value={localToDate}
          onChange={handleToDateChange}
          min={localFromDate}
          max={getTodayString()}
          className="input max-w-[150px]"
        />
      </div>
    </div>
  );
}
